import {
  resolveAccountEntryNextView,
  type ThirdPartyRegisterInitialView,
  validateAccountEntry,
  validateEmailValue,
  validatePasswordPair,
  validatePhoneInput,
  validateSinglePassword,
  type AccountFlowView,
} from '../utils/account-auth-flow'
import { buildGeetestSendCodePayload, buildGraphCodeSendCodePayload } from '../utils/login-sms'

const ACCOUNT_LOGIN_VERSION = 124
const ACCOUNT_APPLICATION_TYPE = 7

const createTimerController = () => {
  const countDown = ref(0)
  const isCountdown = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    countDown.value = 0
    isCountdown.value = false
  }

  const start = (seconds = 60) => {
    clear()
    countDown.value = seconds
    isCountdown.value = true

    const tick = () => {
      if (countDown.value <= 1) {
        clear()
        return
      }

      timer = setTimeout(() => {
        countDown.value -= 1
        tick()
      }, 1000)
    }

    tick()
  }

  const stop = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return {
    countDown,
    isCountdown,
    start,
    clear,
    stop,
  }
}

export const useAccountAuthFlow = () => {
  const appStore = useAppStore()
  const route = useRoute()
  const { $toast } = useNuxtApp()
  const matomo = useMatomo()
  const { invokeV3 } = useLoginApi()
  const { accessCookie } = useLogin()
  const { ensureSelectedCountry } = useLoginCountries()
  const { t } = useAppI18n()

  const currentCountry = computed(() => appStore.usergetCountry)
  const view = ref<AccountFlowView>('accountEntry')
  const loading = ref(false)
  const showRegisterGeetest = ref(false)
  const showRegisterGraphical = ref(false)
  const resetPreviousView = ref<AccountFlowView>('accountEntry')
  const registerBindBackView = ref<AccountFlowView>('registerCreatePassword')

  const registerTimer = createTimerController()
  const resetTimer = createTimerController()

  const form = reactive({
    account: '',
    accountError: '',
    loginPassword: '',
    loginPasswordError: '',
    registerPassword: '',
    registerPasswordConfirm: '',
    registerPasswordError: '',
    registerPhone: '',
    registerPhoneError: '',
    registerSmsCode: '',
    registerSmsError: '',
    registerFiguresmsError: '',
    resetEmail: '',
    resetEmailError: '',
    resetCode: '',
    resetCodeError: '',
    resetPassword: '',
    resetPasswordConfirm: '',
    resetPasswordError: '',
  })

  const requestState = reactive({
    registerSmsRequestId: '',
    resetRequestId: '',
  })

  const isThirdPartyPhoneOnlyRegister = computed(() => route.query.type === 'register' && !form.account.trim())
  const registerSmsBusinessType = computed(() => (isThirdPartyPhoneOnlyRegister.value ? 1 : 8))

  const clearAccountErrors = () => {
    form.accountError = ''
    form.loginPasswordError = ''
  }

  const clearRegisterErrors = () => {
    form.registerPasswordError = ''
    form.registerPhoneError = ''
    form.registerSmsError = ''
    form.registerFiguresmsError = ''
  }

  const clearResetErrors = () => {
    form.resetEmailError = ''
    form.resetCodeError = ''
    form.resetPasswordError = ''
  }

  const resetRegisterState = () => {
    form.registerPassword = ''
    form.registerPasswordConfirm = ''
    form.registerPhone = ''
    form.registerSmsCode = ''
    requestState.registerSmsRequestId = ''
    registerBindBackView.value = 'registerCreatePassword'
    clearRegisterErrors()
    registerTimer.clear()
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
  }

  const resetResetState = () => {
    form.resetEmail = ''
    form.resetCode = ''
    form.resetPassword = ''
    form.resetPasswordConfirm = ''
    requestState.resetRequestId = ''
    resetPreviousView.value = 'accountEntry'
    clearResetErrors()
    resetTimer.clear()
  }

  const reset = () => {
    view.value = 'accountEntry'
    loading.value = false
    form.account = ''
    form.loginPassword = ''
    clearAccountErrors()
    resetRegisterState()
    resetResetState()
  }

  const backToAccountEntry = () => {
    view.value = 'accountEntry'
    form.loginPassword = ''
    form.loginPasswordError = ''
    resetRegisterState()
    resetResetState()
  }

  // 清空已校验账号并回到账号输入态，用于密码态账号行的清除按钮。
  const clearAccountAndBackToEntry = () => {
    backToAccountEntry()
    form.account = ''
    form.accountError = ''
  }

  const backFromRegister = () => {
    if (view.value === 'registerBindPhone') {
      if (registerBindBackView.value === 'registerCreatePassword') {
        view.value = 'registerCreatePassword'
      } else {
        backToAccountEntry()
      }
      form.registerPhoneError = ''
      form.registerSmsError = ''
      return
    }

    backToAccountEntry()
  }

  const backFromReset = () => {
    if (view.value === 'resetNewPassword') {
      view.value = 'resetVerifyEmail'
      form.resetPassword = ''
      form.resetPasswordConfirm = ''
      form.resetPasswordError = ''
      return
    }

    const previousView = resetPreviousView.value
    form.account = form.resetEmail || form.account
    resetResetState()
    view.value = previousView === 'accountPasswordLogin' ? 'accountPasswordLogin' : 'accountEntry'
  }

  const openResetFlow = async () => {
    resetPreviousView.value = view.value === 'accountPasswordLogin' ? 'accountPasswordLogin' : 'accountEntry'
    clearResetErrors()
    resetTimer.clear()
    requestState.resetRequestId = ''
    form.resetCode = ''
    form.resetPassword = ''
    form.resetPasswordConfirm = ''
    form.resetEmail = form.account.includes('@') ? form.account.trim() : ''
    view.value = 'resetVerifyEmail'
    matomo.trackEvent('af_forgot_password')

    if (form.resetEmail) {
      await sendResetCode()
    }
  }

  const startThirdPartyRegister = (
    email = '',
    initialView: ThirdPartyRegisterInitialView = 'registerCreatePassword',
  ) => {
    reset()
    if (!initialView) {
      return false
    }

    const normalizedEmail = email.trim()
    if (!normalizedEmail && initialView !== 'registerBindPhone') {
      return false
    }

    form.account = initialView === 'registerBindPhone' ? '' : normalizedEmail
    registerBindBackView.value = initialView === 'registerBindPhone' ? 'accountEntry' : 'registerCreatePassword'
    view.value = initialView
    return true
  }

  const submitAccountEntry = async () => {
    const validationMessage = validateAccountEntry(form.account, t)
    if (validationMessage) {
      form.accountError = validationMessage
      return false
    }

    loading.value = true
    form.accountError = ''

    const result = await invokeV3('usercenter$accountisregister', {
      account: form.account.trim(),
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.accountisregister
    const nextView = resolveAccountEntryNextView({
      account: form.account.trim(),
      isRegistered: Boolean(node?.data),
    })

    if (nextView === 'unsupportedAccount') {
      form.accountError = t('84006')
      return false
    }

    view.value = nextView
    return true
  }

  const submitPasswordLogin = async () => {
    const validationMessage = validateSinglePassword(form.loginPassword, t)
    if (validationMessage) {
      form.loginPasswordError = validationMessage
      return false
    }

    loading.value = true
    form.loginPasswordError = ''

    const result = await invokeV3('usercenter$newusercenterloginv2', {
      account: form.account.trim(),
      password: form.loginPassword.trim(),
      version: ACCOUNT_LOGIN_VERSION,
      loginType: form.account.includes('@') ? 1 : 0,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      ip: appStore.userIp,
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.newusercenterloginv2
    if (!node?.success) {
      form.loginPasswordError = t('82932')
      return false
    }

    matomo.trackEvent('af_login_success', { af_type: '账号' })
    await accessCookie(node.data)
    return true
  }

  const submitRegisterCreatePassword = async () => {
    const validationMessage = validatePasswordPair(form.registerPassword, form.registerPasswordConfirm, t)
    if (validationMessage) {
      form.registerPasswordError = validationMessage
      matomo.trackEvent('af_login_password', {
        af_judge: '否',
        af_type: validationMessage,
      })
      return false
    }

    form.registerPasswordError = ''
    registerBindBackView.value = 'registerCreatePassword'
    view.value = 'registerBindPhone'
    matomo.trackEvent('af_login_password', {
      af_judge: '是',
      af_type: '',
    })

    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '邮箱账号'
    if (loginTypeRaw.includes('wechat')) afType = '微信'
    else if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'

    matomo.trackEvent('af_login_bind_pop', {
      af_type: afType,
    })

    return true
  }

  const sendRegisterCodeRequest = async (payload: Record<string, any>) => {
    const result = await invokeV3('usercenter$sendcodegeettest4', payload).catch(() => null)

    const node = result?.usercenter?.sendcodegeettest4
    if (!node?.success) {
      const message = node?.extra || 'error'
      if (showRegisterGraphical.value) {
        form.registerFiguresmsError = message
      } else {
        form.registerSmsError = message
      }
      matomo.trackEvent('af_login_bind_vercode', {
        af_judge: '否',
        af_type: message,
      })
      return false
    }

    requestState.registerSmsRequestId = node.data?.requestid || ''
    form.registerSmsError = ''
    form.registerFiguresmsError = ''
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
    matomo.trackEvent('af_login_bind_vercode', {
      af_judge: '是',
      af_type: '',
    })
    registerTimer.start()
    return true
  }

  const sendRegisterCode = async () => {
    if (loading.value || registerTimer.isCountdown.value) return

    const phoneError = validatePhoneInput(form.registerPhone.trim(), currentCountry.value.code, t)
    if (phoneError) {
      form.registerPhoneError = phoneError
      matomo.trackEvent('af_login_bind_vercode', {
        af_judge: '否',
        af_type: phoneError,
      })
      return
    }

    form.registerPhoneError = ''
    form.registerSmsError = ''

    if (!isThirdPartyPhoneOnlyRegister.value) {
      const repeatCheck = await invokeV3('usercenter$newusercentergetrepeatphonepasswordverifyv2', {
        passWord: form.registerPassword,
        phone: form.registerPhone.trim(),
        languageCode: appStore.LanguageCode,
        areaCode: currentCountry.value.code,
        buinsessType: 2,
      }).catch(() => null)

      const repeatNode = repeatCheck?.usercenter?.newusercentergetrepeatphonepasswordverifyv2
      if (!repeatNode?.success) {
        form.registerPhoneError = repeatNode?.extra
        matomo.trackEvent('af_login_bind_vercode', {
          af_judge: '否',
          af_type: repeatNode?.extra,
        })
        return
      }
    }

    if (route.query.type === 'register') {
      const verified = await verifyThirdPartyPhone()
      if (!verified) return
    }

    showRegisterGeetest.value = true
  }

  const handleRegisterGeetestSuccess = async (payload: Record<string, any>) => {
    await sendRegisterCodeRequest(
      buildGeetestSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.registerPhone.trim(),
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: registerSmsBusinessType.value,
          applicationType: ACCOUNT_APPLICATION_TYPE,
        },
        payload as any,
      ),
    )
  }

  const handleRegisterGeetestError = () => {
    showRegisterGeetest.value = false
    showRegisterGraphical.value = true
  }

  const handleRegisterGraphicalSubmit = async (graphCode: string) => {
    await sendRegisterCodeRequest(
      buildGraphCodeSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.registerPhone.trim(),
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: registerSmsBusinessType.value,
          applicationType: ACCOUNT_APPLICATION_TYPE,
        },
        graphCode,
      ),
    )
  }

  const closeRegisterCaptchaLayers = () => {
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
  }

  const verifyThirdPartyPhone = async () => {
    const result = await invokeV3('usercenter$thirdphoneverifyv3', {
      AreaCode: currentCountry.value.code,
      PhoneNubmer: form.registerPhone.trim(),
      AccountType: String(route.query.logintype || ''),
    }).catch(() => null)

    const node = result?.usercenter?.thirdphoneverifyv3
    if (!node?.success) {
      form.registerPhoneError = node?.extra
      return false
    }

    form.registerPhoneError = ''
    return true
  }

  const thirdPartyEmailRegister = async () => {
    const result = await invokeV3('usercenter$thirdemialregisterv3', {
      openId: String(route.query.id || ''),
      accountNick: String(route.query.name || ''),
      accountHead: String(route.query.img || ''),
      accountType: String(route.query.logintype || ''),
      email: form.account.trim(),
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 311,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
      password: form.registerPassword,
    }).catch(() => null)

    const node = result?.usercenter?.thirdemialregisterv3
    if (!node?.success) {
      form.registerSmsError = node?.extra
      return false
    }

    form.registerSmsError = ''
    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '微信'
    if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'
    else if (loginTypeRaw.includes('facebook')) afType = 'facebook'
    matomo.trackEvent('af_login_success', { af_type: afType })
    await accessCookie(node.data)
    return true
  }

  const thirdPartyPhoneRegister = async () => {
    const result = await invokeV3('usercenter$thridphoneregisterv3', {
      openId: String(route.query.id || ''),
      accountNick: String(route.query.name || ''),
      accountHead: String(route.query.img || ''),
      accountType: String(route.query.logintype || ''),
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 112,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
    }).catch(() => null)

    const node = result?.usercenter?.thridphoneregisterv3
    if (!node?.success) {
      form.registerSmsError = node?.extra
      return false
    }

    form.registerSmsError = ''
    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '微信'
    if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'
    else if (loginTypeRaw.includes('facebook')) afType = 'facebook'
    matomo.trackEvent('af_login_success', { af_type: afType })
    await accessCookie(node.data)
    return true
  }

  const submitRegisterBindPhone = async () => {
    const phoneError = validatePhoneInput(form.registerPhone.trim(), currentCountry.value.code, t)
    if (phoneError) {
      form.registerPhoneError = phoneError
      matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: phoneError })
      return false
    }

    if (!form.registerSmsCode.trim()) {
      form.registerSmsError = t('82403')
      matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: t('82403') })
      return false
    }

    loading.value = true
    form.registerPhoneError = ''
    form.registerSmsError = ''

    if (route.query.type === 'register') {
      const phoneVerified = await verifyThirdPartyPhone()
      const success = phoneVerified
        ? isThirdPartyPhoneOnlyRegister.value
          ? await thirdPartyPhoneRegister()
          : await thirdPartyEmailRegister()
        : false
      loading.value = false
      if (success) {
        matomo.trackEvent('af_login_bind_click', { af_judge: '是', af_type: '' })
      } else if (phoneVerified) {
        // thirdPartyEmailRegister already sets error if it fails, but we don't have the message returned.
        // thirdPartyEmailRegister actually does not return the message but sets form.registerSmsError.
        matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: form.registerSmsError })
      } else {
        // verifyThirdPartyPhone failed
        matomo.trackEvent('af_login_bind_click', {
          af_judge: '否',
          af_type: form.registerPhoneError || form.registerSmsError,
        })
      }
      return success
    }

    const result = await invokeV3('usercenter$emailregisterv3', {
      areaCode: currentCountry.value.code,
      password: form.registerPassword,
      passwordRepeat: form.registerPasswordConfirm,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      Email: form.account.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 311,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.emailregisterv3
    if (!node?.success) {
      form.registerSmsError = node?.extra
      matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: node?.extra })
      return false
    }

    matomo.trackEvent('af_login_success', { af_type: '邮箱' })
    await accessCookie(node.data)
    matomo.trackEvent('af_login_bind_click', { af_judge: '是', af_type: '' })
    return true
  }

  const sendResetCode = async () => {
    if (loading.value || resetTimer.isCountdown.value) return
    matomo.trackEvent('af_forgot_vercode')

    if (!form.resetEmail.trim()) {
      form.resetEmailError = t('82411')
      return
    }

    if (validateEmailValue(form.resetEmail, t)) {
      form.resetEmailError = t('82928')
      return
    }

    const existsResponse = await invokeV3('usercenter$newusercenteremailisexistv2', {
      email: form.resetEmail.trim(),
      languageCode: appStore.LanguageCode,
    }).catch(() => null)

    const existsNode = existsResponse?.usercenter?.newusercenteremailisexistv2
    if (!existsNode?.success || !existsNode?.data) {
      form.resetEmailError = existsNode?.extra
      return
    }

    const sendResponse = await invokeV3('usercenter$newusercentersendemailv2', {
      email: form.resetEmail.trim(),
      emailType: 3,
      languageCode: appStore.LanguageCode,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      version: ACCOUNT_LOGIN_VERSION,
    }).catch(() => null)

    const sendNode = sendResponse?.usercenter?.newusercentersendemailv2
    if (!sendNode?.success) {
      form.resetEmailError = sendNode?.extra
      return
    }

    form.resetEmailError = ''
    form.resetCodeError = ''
    resetTimer.start()
  }

  const submitResetVerifyEmail = async () => {
    matomo.trackEvent('af_forgot_next')

    if (!form.resetEmail.trim()) {
      form.resetEmailError = '请先输入邮箱'
      return false
    }

    if (validateEmailValue(form.resetEmail, t)) {
      form.resetEmailError = t('82928')
      return false
    }

    if (!form.resetCode.trim()) {
      form.resetCodeError = t('82412')
      return false
    }

    loading.value = true
    clearResetErrors()

    const result = await invokeV3('usercenter$newusercentervalidateemailcodev2', {
      Email: form.resetEmail.trim(),
      LanguageCode: appStore.LanguageCode,
      Code: form.resetCode.trim(),
      ApplicationType: ACCOUNT_APPLICATION_TYPE,
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.newusercentervalidateemailcodev2
    if (!node?.success) {
      form.resetCodeError = t('84053')
      return false
    }

    requestState.resetRequestId = node.data?.requestId || ''
    form.account = form.resetEmail.trim()
    view.value = 'resetNewPassword'
    matomo.trackEvent('af_reset_password')
    return true
  }

  const submitResetNewPassword = async () => {
    matomo.trackEvent('af_reset_click')

    if (form.resetPassword !== form.resetPasswordConfirm) {
      form.resetPasswordError = t('82930')
      return false
    }

    const validationMessage = validatePasswordPair(form.resetPassword, form.resetPasswordConfirm, t)
    if (validationMessage) {
      form.resetPasswordError = validationMessage
      return false
    }

    loading.value = true
    form.resetPasswordError = ''

    const result = await invokeV3('usercenter$newusercenteforrgetuserpassv2', {
      npwd: form.resetPassword,
      requestId: requestState.resetRequestId,
      languageCode: appStore.LanguageCode,
      email: form.resetEmail.trim(),
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.newusercenteforrgetuserpassv2
    if (!node?.success) {
      form.resetPasswordError = node?.extra
      return false
    }

    form.account = form.resetEmail.trim()
    $toast(1, t('82929'))
    reset()
    return true
  }

  const submit = async () => {
    if (loading.value) return false

    if (view.value === 'accountEntry') return await submitAccountEntry()
    if (view.value === 'accountPasswordLogin') return await submitPasswordLogin()
    if (view.value === 'registerCreatePassword') return await submitRegisterCreatePassword()
    if (view.value === 'registerBindPhone') return await submitRegisterBindPhone()
    if (view.value === 'resetVerifyEmail') return await submitResetVerifyEmail()
    return await submitResetNewPassword()
  }

  const isPrimaryEnabled = computed(() => {
    if (view.value === 'accountEntry') return form.account.trim().length >= 5
    if (view.value === 'accountPasswordLogin') return Boolean(form.loginPassword.trim())
    if (view.value === 'registerCreatePassword') return Boolean(form.registerPassword && form.registerPasswordConfirm)
    if (view.value === 'registerBindPhone') return Boolean(form.registerPhone.trim() && form.registerSmsCode.trim())
    if (view.value === 'resetVerifyEmail') return Boolean(form.resetEmail.trim() && form.resetCode.trim())
    return form.resetPassword.length >= 6 && form.resetPasswordConfirm.length >= 6
  })

  onMounted(() => {
    ensureSelectedCountry()
  })

  onBeforeUnmount(() => {
    registerTimer.stop()
    resetTimer.stop()
  })

  return {
    currentCountry,
    form,
    isPrimaryEnabled,
    loading,
    openResetFlow,
    closeRegisterCaptchaLayers,
    registerCountdown: registerTimer.countDown,
    registerFiguresmsError: computed(() => form.registerFiguresmsError),
    registerGraphicalVisible: showRegisterGraphical,
    registerGeetestVisible: showRegisterGeetest,
    registerIsCountdown: registerTimer.isCountdown,
    reset,
    resetCountdown: resetTimer.countDown,
    resetIsCountdown: resetTimer.isCountdown,
    handleRegisterGeetestError,
    handleRegisterGeetestSuccess,
    handleRegisterGraphicalSubmit,
    sendRegisterCode,
    sendResetCode,
    startThirdPartyRegister,
    submit,
    view,
    backFromRegister,
    backFromReset,
    backToAccountEntry,
    clearAccountAndBackToEntry,
  }
}
